import React from 'react';
import PropTypes from 'prop-types';
import tw, { styled } from 'twin.macro';
import chroma from 'chroma-js';

import { motion } from '@sopranodesign/framer-motion';
import { pseudoStyles } from '@sopranodesign/pulse-components';

const getGradient = (colour, darken = 0.2) => {
  if (!colour) {
    return;
  }
  return `linear-gradient(to right bottom,
    ${chroma(colour).brighten(darken)},${chroma(colour).darken(darken)})`;
};

const StyledBubble = styled(motion.div)`
  ${tw`flex flex-col text-sm whitespace-pre-wrap`};
  line-height: normal;
  border-radius: 0.675rem;
  box-shadow: ${props => {
    return chroma(
      props.theme?.bot[props.direction].background || props.theme.primary
    )
      .darken()
      .desaturate()
      .alpha(0.25);
  }}
    0px 8px 12px -4px;
  padding: 0.625rem 0.75rem;
  color: ${props => props.theme.bot[props.direction].text};
  background-color: ${props => {
    return props.theme?.bot[props.direction].background || props.theme.primary;
  }};
  background-image: ${props => {
    return getGradient(props.theme.bot[props.direction].background);
  }};
  width: fit-content;
  min-width: 0;
  max-width: 75%;
  overflow-wrap: break-word;
  overflow: hidden;
  min-height: 1rem;
  border: 1px solid transparent;

  & .message-type-markdown {
    ${tw`whitespace-normal`}
  }

  & .message-type-markdown > div > p {
    ${tw`my-0`}
  }

  & .message-type-markdown > div > p:not(:first-of-type) {
    ${tw`mt-4`}
  }

  & .message-type-markdown > div > div {
    ${tw`mt-4 mb-0`}
  }

  & .message-type-markdown-left > div > h1,
  & .message-type-markdown-left > div > h2,
  & .message-type-markdown-left > div > h3,
  & .message-type-markdown-left > div > h4,
  & .message-type-markdown-left > div > h5,
  & .message-type-markdown-left > div > h6
  {
    margin:revert;
  }

  & .message-type-markdown-left > div a {
    ${props => props.theme?.bot?.sentMessage?.link &&
    `color: ${props.theme.bot.sentMessage.link};`}
  }

  & .message-type-markdown-left > div > pre,
  & .message-type-markdown-left > div > p > code,
  & .message-type-markdown-left > div > h1 > code
  {
    background: ${props => {
    const data = props.theme?.bot['sentMessage'].background || props.theme.primary;
    return chroma(data).alpha(0.2);
  }};
  }

  & .message-type-markdown-right table,
  & .message-type-markdown-right > div > pre,
  & .message-type-markdown-right > div > p > code,
  & .message-type-markdown-right > div > h1 > code,
  & .message-type-markdown-right > div [type="note"] {
    background: ${props => {
    const data = props.theme?.bot['recievedMessage'].background || props.theme.primary;
    return chroma(data).alpha(0.2);
  }};
  }

  ${props => (props.direction === 'sentMessage' ? pseudoStyles : '')};
`;

const Bubble = ({ children, direction, tabIndex = 0, ...props }) => (
  <StyledBubble
    data-testid="bubble"
    direction={direction}
    style={{ originX: direction === 'sentMessage' ? 0 : 1, originY: 1 }}
    initial={{ scale: 0 }}
    animate={{ scale: 1 }}
    transition={{ type: 'spring', stiffness: 400, damping: 50 }}
    tabIndex={tabIndex}
    {...props}
  >
    {children}
  </StyledBubble>
);

Bubble.propTypes = {
  direction: PropTypes.oneOf(['recievedMessage', 'sentMessage']),
  tabIndex: PropTypes.number
};

export default Bubble;